/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Model a teacher's intervention as a bounded session, not a permanent takeover.
 * @state-owner live intervention store
 * @allowed-imports none
 * @forbidden React; network; store implementations
 * @public-api live module
 * @side-effects none
 * @tests modules/live/model/intervention.test.ts
 */

/**
 * Taking the whole conversation is one option among four. A teacher who only wants to leave a
 * private note, or to handle it face to face, should not have to seize the channel to do it.
 */
/**
 * `inPerson`, not `offline`: `offline` already means "the student is not connected" in
 * SeatStatus, and the two would sit side by side in this module meaning opposite things.
 */
export type InterventionMode = 'join' | 'pause' | 'note' | 'inPerson';

export const MODE_LABEL: Readonly<Record<InterventionMode, string>> = {
  join: '加入對話',
  pause: '暫停助教',
  note: '私下提示',
  inPerson: '當面處理',
};

export const MODE_HINT: Readonly<Record<InterventionMode, string>> = {
  join: '學生看得到你，助教退到只記錄',
  pause: '助教先停，你稍後再決定怎麼接',
  note: '只有學生看得到你的提示，助教不知道',
  inPerson: '你走過去講，這裡只留下紀錄',
};

/** Whether the assistant may still speak while the teacher is in. It never competes for the turn. */
export function assistantSpeaks(mode: InterventionMode): boolean {
  return mode === 'note' || mode === 'inPerson';
}

export function studentSeesTeacher(mode: InterventionMode): boolean {
  return mode !== 'inPerson';
}

export interface InterventionSession {
  readonly id: string;
  readonly student: string;
  readonly mode: InterventionMode;
  readonly startedAt: string;
  readonly endedAt?: string;
  /** What the teacher tells the assistant before handing the student back. */
  readonly handBackNote?: string;
  /** Ask the student to say it back in their own words before the assistant resumes. */
  readonly askRestate: boolean;
  readonly restatement?: string;
}

export function startSession(
  id: string,
  student: string,
  mode: InterventionMode,
  at: string,
): InterventionSession {
  return { id, student, mode, startedAt: at, askRestate: true };
}

/**
 * A session only closes once the student has said it back, when that was asked for. Solving the
 * question is not the same as the student being able to carry it.
 */
export function canHandBack(session: InterventionSession): boolean {
  return !session.askRestate || Boolean(session.restatement?.trim());
}

export function sessionSummary(session: InterventionSession): string {
  const span = session.endedAt
    ? `${session.startedAt}–${session.endedAt}`
    : `${session.startedAt}–`;
  return `${span} · ${MODE_LABEL[session.mode]}`;
}
